import React, { useState, useEffect, useRef } from 'react';
import { Search, RefreshCw, AlertCircle } from 'lucide-react';
import { woocommerceAPI } from '../../api';

const WebOrderLookup = ({
  onItemsFetched,
  onCustomerInfo,
  fetchedOrderRefs = [],
  onOrderRefAdded,
  returnedItems = [],
  disabled = false,
  usePaidPrice = false,
}) => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [orderId, setOrderId] = useState('');
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Fetch available brands on mount
  useEffect(() => {
    woocommerceAPI.getBrands().then(res => {
      const brandList = res.data;
      setBrands(brandList);
      if (brandList.length === 1) {
        setSelectedBrand(brandList[0]);
      }
    }).catch(() => {
      setError('Failed to load brands');
    });
  }, []);

  // Auto-focus order ID input when brand is selected
  useEffect(() => {
    if (selectedBrand && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedBrand]);

  const handleFetch = async () => {
    if (!selectedBrand || !orderId) return;

    const refKey = `${selectedBrand}:${orderId}`;
    const alreadyFetched = fetchedOrderRefs.some(r => `${r.brand}:${r.woocommerce_order_id}` === refKey);
    const hasItemsFromOrder = returnedItems.some(
      item => item.webOrderRef && `${item.webOrderRef.brand}:${item.webOrderRef.woocommerce_order_id}` === refKey
    );

    if (alreadyFetched && hasItemsFromOrder) {
      setError('This order has already been fetched');
      return;
    }

    try {
      setFetching(true);
      setError('');

      const res = await woocommerceAPI.getOrder(selectedBrand, orderId);
      const order = res.data;

      const availableItems = order.line_items.filter(item => item.available_quantity > 0);

      if (availableItems.length === 0) {
        setError('All items in this order have already been returned');
        return;
      }

      const newItems = availableItems.map(item => ({
        Item_ID: item.item_id,
        item: {
          ID: item.item_id,
          Name: item.name,
          Price: item.price,
          SalePrice: item.sale_price,
          brand: item.brand,
          product_id: item.product_id,
        },
        quantity: item.available_quantity,
        ordered: item.quantity,
        alreadyReturned: item.already_returned,
        price: usePaidPrice ? parseFloat(item.paid_unit_price) : parseFloat(item.sale_price),
        discount: usePaidPrice ? 0 : parseFloat(item.discount),
        // Order-level discount WooCommerce kept out of the line prices (negative fee lines),
        // pre-split per unit by the API. Consumed by the sale return screen; harmless elsewhere.
        discountShareUnit: parseFloat(item.discount_share_unit || 0),
        actualPrice: parseFloat(item.price),
        maxQuantity: item.available_quantity,
        webOrderRef: { brand: selectedBrand, woocommerce_order_id: parseInt(orderId) },
      }));

      onItemsFetched(newItems);
      onOrderRefAdded({ brand: selectedBrand, woocommerce_order_id: parseInt(orderId) });

      if (order.customer_name) {
        onCustomerInfo({ name: order.customer_name, phone: order.customer_phone || '' });
      }

      setOrderId('');
      if (inputRef.current) inputRef.current.focus();

    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.message) {
        setError(`${detail.message}: ${detail.unmatched_items?.map(i => i.name).join(', ')}`);
      } else {
        setError(detail || 'Failed to fetch order');
      }
    } finally {
      setFetching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFetch();
    }
    // Backtick (`) to cycle through brands
    if (e.key === '`' && brands.length > 1) {
      e.preventDefault();
      const currentIdx = brands.indexOf(selectedBrand);
      const nextIdx = (currentIdx + 1) % brands.length;
      setSelectedBrand(brands[nextIdx]);
    }
  };

  return (
    <div className="space-y-3">
      {/* Brand Selector */}
      {brands.length > 1 && (
        <div className="flex items-center gap-2">
          {brands.map((b, idx) => (
            <button
              key={b}
              type="button"
              onClick={() => setSelectedBrand(b)}
              disabled={disabled}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                selectedBrand === b
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title="Press ` to switch brand"
            >
              {b.charAt(0).toUpperCase() + b.slice(1)}
            </button>
          ))}
        </div>
      )}
      {brands.length === 1 && (
        <div className="text-sm text-gray-500">
          Brand: <span className="font-medium text-gray-900">{brands[0].charAt(0).toUpperCase() + brands[0].slice(1)}</span>
        </div>
      )}

      {/* Order ID Input + Fetch */}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="number"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="WooCommerce Order ID"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={handleFetch}
          disabled={disabled || fetching || !selectedBrand || !orderId}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          title="Fetch Order"
        >
          {fetching ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <Search className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Fetched Orders */}
      {fetchedOrderRefs.length > 0 && (
        <div className="text-xs text-gray-500">
          Fetched: {fetchedOrderRefs.map(r => `${r.brand} #${r.woocommerce_order_id}`).join(', ')}
        </div>
      )}
    </div>
  );
};

export default WebOrderLookup;
